import React, {
  useMemo,
  useRef,
} from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { format, parse } from 'date-fns';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_ATTRIBUTION,
  ESRI_SATELLITE_TILE_URL,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import BrazilCountryLayer from 'components/InteractiveMap/BrazilCountryLayer';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import Button from 'components/DesignSystem/Button';
import AgroMapHoverCard from 'components/Meteorology/Agro/Maps/AgroMapHoverCard';
import AgroMapLayersPanel from 'components/Meteorology/Agro/Maps/AgroMapLayersPanel';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import { CustomAlert } from 'components/AlertsCollection';

import { CultureIndexAreaMap } from 'types/Meteorology/Agro/CultureIndex/Area/map';

import useMapsControl from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapsControl';
import useMapLocationMarkers from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapLocationMarkers';
import useEnrichedMarkers from 'hooks/Meteorology/Agro/CultureIndex/Area/useEnrichedMarkers';
import useMapDownload from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapDownload';
import useCaption from 'hooks/Meteorology/Agro/CultureIndex/Area/useCaption';
import useHeatmapConfig from 'hooks/Meteorology/Agro/CultureIndex/Area/useHeatmapConfig';
import useHeatmapLegend from 'hooks/Meteorology/Agro/CultureIndex/Area/useHeatmapLegend';
import useAgroMapState from 'hooks/Meteorology/Agro/Maps/useAgroMapState';

import { findNearestValue } from 'helper/Meteorology/Agro/Maps/geoUtils';

import './index.scss';

const LAYER_DEFS = [
  { key: 'localidades', label: 'Localidades' },
  { key: 'limitesEstaduais', label: 'Limites estaduais' },
  { key: 'tooltip', label: 'Tooltip de informações' },
];

function buildMapTitle(areaMap: CultureIndexAreaMap, position: number, totalMaps: number): string {
  const cultureName = areaMap.params.culture?.label ?? areaMap.params.culture?.name ?? '';
  const stageLabel = areaMap.params.stage?.label ?? areaMap.params.stage?.name ?? '';
  const stagePart = stageLabel ? `(${stageLabel})` : '';
  const start = areaMap.params.periodStart
    ? format(parse(areaMap.params.periodStart, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
    : '';
  const end = areaMap.params.periodEnd
    ? format(parse(areaMap.params.periodEnd, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
    : '';
  const period = [start, end].every(Boolean) ? `${start} - ${end}` : '';
  const parts = [
    ...(totalMaps > 1 ? [`Mapa ${position}`] : []),
    cultureName,
    stagePart,
    period ? `(${period})` : '',
  ];
  return parts.filter(Boolean).join(' - ');
}

export default function MapDisplay({
  areaMap,
  position,
  totalMaps,
}: {
  areaMap: CultureIndexAreaMap;
  position: number;
  totalMaps: number;
}) {
  const { onDownloadClick, setOnDownloadClick, isDownloadDisabled } = useMapDownload();
  const caption = useCaption(areaMap);
  const { removeMap, removeMapDisabled } = useMapsControl();
  const heatmapConfig = useHeatmapConfig();
  const legend = useHeatmapLegend(areaMap.data, heatmapConfig.gradient as Record<string, string>);

  const hasDataAfterConsulta = areaMap.data !== undefined;
  const showTitleAndCloseButton = totalMaps > 1;
  const locationMarkers = useMapLocationMarkers(areaMap);
  const enrichedMarkers = useEnrichedMarkers(locationMarkers, areaMap.data);
  const divRef = useRef<HTMLDivElement>(null);

  const alert: CustomAlert | undefined = useMemo(() => (areaMap.error ? {
    title: areaMap.error.title,
    message: areaMap.error.message,
    variant: 'danger',
  } : undefined), [areaMap.error]);

  const {
    layers, handleToggle,
    cursorPos, handleMove, handleLeave,
    hoveredState, handleStateHover, handleStateLeave,
    isOverMarker, handleMarkerEnter, handleMarkerLeave,
  } = useAgroMapState(LAYER_DEFS);

  const nearestValue = useMemo(() => {
    if (!cursorPos || !areaMap.data?.length) return null;
    return findNearestValue(cursorPos.lat, cursorPos.lng, areaMap.data);
  }, [cursorPos, areaMap.data]);

  return (
    <Card className="shadow-sm p-4 forecast-instruction-card" ref={divRef}>
      {showTitleAndCloseButton && (
        <div className="d-flex align-items-center mb-3">
          <div className="text-dark-blue me-auto">
            <TitleSemiBold>
              {hasDataAfterConsulta
                ? buildMapTitle(areaMap, position, totalMaps)
                : `Mapa ${position}`}
            </TitleSemiBold>
          </div>
          <Button
            className="me-2"
            icon="download"
            type="tertiary"
            onClick={onDownloadClick}
            disabled={isDownloadDisabled(areaMap)}
          />
          <Button
            icon="close"
            type="tertiary"
            onClick={() => removeMap(areaMap)}
            disabled={removeMapDisabled}
          />
        </div>
      )}
      {!showTitleAndCloseButton && (
        <div className="d-flex align-items-center mb-3 justify-content-end">
          {hasDataAfterConsulta && (
            <div className="text-dark-blue me-auto">
              <TitleSemiBold>{buildMapTitle(areaMap, position, totalMaps)}</TitleSemiBold>
            </div>
          )}
          <Button
            icon="download"
            type="tertiary"
            onClick={onDownloadClick}
            disabled={isDownloadDisabled(areaMap)}
          />
        </div>
      )}
      <div className="ci-map-wrapper">
        <InteractiveMap
          markers={layers.localidades ? enrichedMarkers : []}
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
          onMarkerEnter={handleMarkerEnter}
          onMarkerLeave={handleMarkerLeave}
        >
          <CanvasHeatLayer
            points={(!areaMap.isLoading && areaMap.data) || []}
            config={heatmapConfig}
            legend={legend}
          />
          {layers.limitesEstaduais
            ? (
              <BrazilStatesLayer
                onStateHover={handleStateHover}
                onStateLeave={handleStateLeave}
              />
            )
            : <BrazilCountryLayer />}
          <InteractiveMap.MapHoverTracker onMove={handleMove} onLeave={handleLeave} />
          <InteractiveMap.MapUpdater observedDivRef={divRef} />
          <InteractiveMap.Alert alert={alert} />
          <InteractiveMap.ImageDownloader
            setOnDownload={setOnDownloadClick}
            filename={areaMap.filename}
            caption={caption}
          />
        </InteractiveMap>

        <AgroMapLayersPanel
          layers={layers}
          onToggle={handleToggle}
          definitions={LAYER_DEFS}
        />

        {areaMap.isLoading && (
          <div className="agro-map-loading">
            <Spinner animation="border" />
          </div>
        )}

        <AgroMapHoverCard
          cursorX={isOverMarker || !layers.tooltip ? null : cursorPos?.x ?? null}
          cursorY={isOverMarker || !layers.tooltip ? null : cursorPos?.y ?? null}
          value={nearestValue}
          label="Índice [adm]"
          formatValue={(v) => v.toFixed(2)}
          stateName={hoveredState?.name ?? null}
        />
      </div>
    </Card>
  );
}
